/**
 * Utility functions for proportional discount distribution across line items
 * Ensures mathematical accuracy and proper handling of rounding
 */

export interface LineItemForDiscount {
  id: string;
  quantity: number;
  price: number; // USD base price
  onSalePrice?: number;
  onSale?: boolean;
}

export interface LineItemDiscount {
  itemId: string;
  discountAmount: number;     // USD discount amount for this line item
  discountPercentage: number; // actual percentage applied to this item
  discountedTotal: number;    // line total after discount in USD
}

export interface DistributedDiscount {
  totalDiscountAmount: number;
  lineItemDiscounts: LineItemDiscount[];
  adjustmentMade: boolean; // indicates if rounding adjustment was applied
}

/**
 * Get the effective price for an item (considering sale price)
 */
export const getEffectivePrice = (item: LineItemForDiscount): number => {
  if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
    return item.onSalePrice;
  }
  return item.price;
};

/**
 * Calculate line item total in USD
 */
export const getLineItemTotal = (item: LineItemForDiscount): number => {
  return getEffectivePrice(item) * item.quantity;
};

/**
 * Calculate order subtotal in USD
 */
export const calculateOrderSubtotal = (items: LineItemForDiscount[]): number => {
  return items.reduce((total, item) => total + getLineItemTotal(item), 0);
};

/**
 * Distribute percentage discount across line items
 * Each item gets the same percentage discount
 */
export const distributePercentageDiscount = (
  items: LineItemForDiscount[],
  discountPercentage: number
): DistributedDiscount => {
  const lineItemDiscounts: LineItemDiscount[] = [];
  let totalCalculatedDiscount = 0;

  // Apply percentage to each line item
  for (const item of items) {
    const lineItemTotal = getLineItemTotal(item);
    const itemDiscountAmount = Math.round((lineItemTotal * discountPercentage / 100) * 100) / 100;
    const discountedTotal = Math.max(0, lineItemTotal - itemDiscountAmount);

    lineItemDiscounts.push({
      itemId: item.id,
      discountAmount: itemDiscountAmount,
      discountPercentage: discountPercentage,
      discountedTotal: discountedTotal
    });

    totalCalculatedDiscount += itemDiscountAmount;
  }

  return {
    totalDiscountAmount: Math.round(totalCalculatedDiscount * 100) / 100,
    lineItemDiscounts,
    adjustmentMade: false // No adjustment needed for percentage discounts
  };
};

/**
 * Distribute fixed amount discount across line items proportionally
 * Each item gets a percentage of the discount based on its value relative to total
 */
export const distributeFixedAmountDiscount = (
  items: LineItemForDiscount[],
  fixedDiscountAmount: number
): DistributedDiscount => {
  const orderSubtotal = calculateOrderSubtotal(items);
  
  // If discount exceeds subtotal, cap it
  const actualDiscountAmount = Math.min(fixedDiscountAmount, orderSubtotal);
  
  if (actualDiscountAmount <= 0 || orderSubtotal <= 0) {
    return {
      totalDiscountAmount: 0,
      lineItemDiscounts: items.map(item => ({
        itemId: item.id,
        discountAmount: 0,
        discountPercentage: 0,
        discountedTotal: getLineItemTotal(item)
      })),
      adjustmentMade: false
    };
  }

  const lineItemDiscounts: LineItemDiscount[] = [];
  let totalCalculatedDiscount = 0;

  // Calculate proportional discount for each item
  for (const item of items) {
    const lineItemTotal = getLineItemTotal(item);
    const proportionalPercentage = (lineItemTotal / orderSubtotal) * 100;
    const itemDiscountAmount = Math.round((actualDiscountAmount * (lineItemTotal / orderSubtotal)) * 100) / 100;
    const discountedTotal = Math.max(0, lineItemTotal - itemDiscountAmount);

    lineItemDiscounts.push({
      itemId: item.id,
      discountAmount: itemDiscountAmount,
      discountPercentage: Math.round(proportionalPercentage * 100) / 100,
      discountedTotal: discountedTotal
    });

    totalCalculatedDiscount += itemDiscountAmount;
  }

  // Handle rounding adjustment - add/subtract difference to largest line item
  const roundingDifference = Math.round((actualDiscountAmount - totalCalculatedDiscount) * 100) / 100;
  let adjustmentMade = false;

  if (Math.abs(roundingDifference) >= 0.01) {
    // Find the item with the largest discount amount to apply adjustment
    const largestDiscountIndex = lineItemDiscounts.reduce((maxIndex, current, index) => 
      current.discountAmount > lineItemDiscounts[maxIndex].discountAmount ? index : maxIndex, 0
    );

    const adjustedItem = lineItemDiscounts[largestDiscountIndex];
    const newDiscountAmount = Math.round((adjustedItem.discountAmount + roundingDifference) * 100) / 100;
    const originalLineTotal = getLineItemTotal(items.find(item => item.id === adjustedItem.itemId)!);
    
    lineItemDiscounts[largestDiscountIndex] = {
      ...adjustedItem,
      discountAmount: Math.max(0, Math.min(newDiscountAmount, originalLineTotal)),
      discountedTotal: Math.max(0, originalLineTotal - newDiscountAmount)
    };

    totalCalculatedDiscount = actualDiscountAmount;
    adjustmentMade = true;
  }

  return {
    totalDiscountAmount: actualDiscountAmount,
    lineItemDiscounts,
    adjustmentMade
  };
};

/**
 * Main function to distribute discount across line items
 * Handles both percentage and fixed amount discounts
 */
export const distributeDiscountAcrossItems = (
  items: LineItemForDiscount[],
  discountType: 'percentage' | 'fixed',
  discountValue: number
): DistributedDiscount => {
  if (items.length === 0) {
    return {
      totalDiscountAmount: 0,
      lineItemDiscounts: [],
      adjustmentMade: false
    };
  }

  switch (discountType) {
    case 'percentage':
      return distributePercentageDiscount(items, discountValue);
    case 'fixed':
      return distributeFixedAmountDiscount(items, discountValue);
    default:
      throw new Error(`Unsupported discount type: ${discountType}`);
  }
};

/**
 * Validate that distributed discounts add up correctly
 * Returns true if totals match within acceptable rounding tolerance
 */
export const validateDiscountDistribution = (
  distribution: DistributedDiscount,
  expectedTotal: number,
  tolerance: number = 0.01
): boolean => {
  const calculatedTotal = distribution.lineItemDiscounts.reduce(
    (total, item) => total + item.discountAmount, 
    0
  );
  
  return Math.abs(calculatedTotal - expectedTotal) <= tolerance;
};